import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Shield, Award, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import CallbackForm from "./CallbackForm";
import heroBackground from "@/assets/hero-couverture-background.jpg";
import { useToast } from "@/hooks/use-toast";

const Hero = () => {
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  // Check for callback success parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('callback') === 'success') {
      toast({
        title: "Message envoyé !",
        description: "Nous vous recontacterons dans les plus brefs délais.",
      });
      setShowForm(false);
      
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background Image with parallax effect */}
      <img
        src={heroBackground}
        alt="Couverture toiture professionnelle en Loire-Atlantique"
        className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-700"
      />
      {/* Modern gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/50 via-primary/40 to-secondary/60"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center text-white">
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="space-y-6 animate-fade-in">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6 leading-tight tracking-tight animate-scale-in">
              Jacky Couverture
            </h1>
            <div className="inline-block px-8 py-4 glass rounded-2xl shadow-2xl animate-slide-up">
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold gradient-text">
                Couvreur de l'Atlantique
              </p>
            </div>
          </div>
          <p className="text-xl md:text-2xl mb-10 text-white/95 max-w-3xl mx-auto font-medium leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Expert couverture en Loire-Atlantique (44), Vendée (85) et Maine-et-Loire (49)
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold text-lg">
              <span className="w-3 h-3 bg-green-400 rounded-full"></span>
              Intervention d'urgence sous 2h
            </div>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold text-lg animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Shield className="w-5 h-5 text-yellow-300" />
              Garantie Décennale
            </div>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold text-lg animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <AlertTriangle className="w-5 h-5 text-yellow-300" />
              Urgence Fuite Toiture
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-bounce-in" style={{ animationDelay: '0.5s' }}>
            <Button 
              size="lg" 
              className="text-lg px-12 py-8 bg-white text-primary hover:bg-accent hover:text-white shadow-2xl hover:shadow-accent hover:scale-110 transition-all duration-300 font-bold rounded-xl hover-lift"
              onClick={() => setShowForm(true)}
            >
              <MessageCircle className="mr-3 h-6 w-6" />
              Devis gratuit sous 24h
            </Button>
            
            <Button 
              size="lg" 
              className="text-lg px-12 py-8 glass text-white border-2 border-white/50 hover:bg-white hover:text-primary shadow-2xl hover:scale-110 transition-all duration-300 font-bold rounded-xl hover-lift"
              asChild
            >
              <a href="tel:0646702617">
                <Phone className="mr-3 h-6 w-6" />
                06.46.70.26.17
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Callback Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-2 top-2 z-10"
              onClick={() => setShowForm(false)}
            >
              ×
            </Button>
            <CallbackForm onClose={() => setShowForm(false)} />
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;